import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import Navbar from '../navbar';
import Sidebar from '../sidebar/index';
import Typography from '../../components/Typography';
import CustomMessageList from '../../components/CustomMessageList';

// Initialize Socket.IO client with authentication
const socket = io(`${process.env.REACT_APP_BACKEND_URL}`, {
  reconnection: true,
  reconnectionAttempts: 5,
  auth: {
    token: localStorage.getItem('token'),
  },
});

const TicketChatPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chatContainerRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };
  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      const userInfo = localStorage.getItem('userInfo');
      if (!token || !userInfo) {
        navigate('/login');
        return;
      }
      setUser(JSON.parse(userInfo));
    };
    fetchUser();
  }, [navigate]);

  // Fetch ticket data and messages
  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const [ticketResponse, messagesResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ticket/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ticket/${id}/messages`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const ticketData = ticketResponse.data.ticket;
        ticketData.messages = messagesResponse.data.messages || [];
        setTicket(ticketData);
        setLoading(false);

        // Join the ticket room
        socket.emit('join_ticket', { ticketId: id });
      } catch (err) {
        setError(err.message);
        setLoading(false);
        if (err.message.includes('token')) navigate('/login');
      }
    };
    fetchTicket();

    return () => {
      socket.emit('leave_ticket', { ticketId: id });
      socket.off('receive_message');
      socket.off('error');
    };
  }, [id, navigate]);

  // Socket.IO: Listen for new messages
  useEffect(() => {
    socket.on('receive_message', (message) => {
      if (message.ticketId === id) {
        setTicket((prevTicket) => ({
          ...prevTicket,
          messages: [...(prevTicket.messages || []), message],
        }));
      }
    });

    socket.on('error', (data) => {
      setError(data.message);
    });

    return () => {
      socket.off('receive_message');
      socket.off('error');
    };
  }, [id]);

  // Scroll to bottom when messages update
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [ticket?.messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageData = {
      ticketId: id,
      content: newMessage,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    socket.emit('send_message', messageData);
    setNewMessage('');
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).replace(/ /g, ' ');
    } catch {
      return 'Invalid Date';
    }
  };

  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Invalid Time';
    }
  };

  if (!user || loading) return <div className="text-white text-center">Loading...</div>;
  if (error) return <div className="text-red-500 text-center">Error: {error}</div>;
  if (!ticket) return <div className="text-white text-center">Ticket not found.</div>;

  return (
    <div>
      <Navbar user={user} isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        closeSidebar={closeSidebar} />
      <div className="flex"  >
        <Sidebar isOpen={isSidebarOpen} closeSidebar={closeSidebar} />
        <div className="w-full h-full min-h-screen sm:p-5 bg-[#121212] border-[#353535] border-l">
          <div className="bg-black rounded p-6">
            <div className="mb-6">
              <Typography
                text={`Ticket No. ${ticket.ticketNo}`}
                transform="uppercase"
                color="#FFF"
                size="24px"
                weight={400}
                family="Supply"
              />
              <Typography
                text={`Created on ${formatDate(ticket.createdOn)}`}
                color="#9CA3AF"
                size="14px"
                weight={400}
              />
              <div className='mt-[18px] bg-white opacity-10 h-[1px]'></div>
            </div>

            <div
              className="chat-container bg-black rounded-lg p-6 flex flex-col h-[500px]"

            >
              <div ref={chatContainerRef} className="flex-1 scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto">
                {ticket.messages.map((message, index) => (
                  <div
                    key={index}
                    className={`mb-4 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <CustomMessageList
                      client_avatar={message.sender === 'user' ? process.env.REACT_APP_BACKEND_URL+user?.avatar : ticket.client_avatar}
                      message={message.content}
                      isSupport={message.sender === 'admin'}
                      timestamp={formatTime(message.timestamp)}
                    />
                  </div>
                ))}
              </div>

              <form onSubmit={handleSendMessage} className="mt-4 flex items-center">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Ask us anything"
                  style={{ fontFamily: 'Aeonik' }}
                  className="h-[40px] outline-none border-none w-full border-b border-[1px] border-[#4C4C4C] text-white opacity-40 bg-transparent text-[14px]"
                  required
                />
                <button
                  type="submit"
                  className="relative flex items-center justify-center w-8 h-8 transition-all duration-300"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className='absolute inset-0 w-full h-full' width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M0 2C0 0.895431 0.895431 0 2 0H23.2485C23.733 0 24.201 0.175836 24.5655 0.494847L31.317 6.40238C31.751 6.78216 32 7.33081 32 7.90754V30C32 31.1046 31.1046 32 30 32H2C0.895431 32 0 31.1046 0 30V2Z" fill="#DF0F11" />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" className='relative flex items-center justify-center z-10 space-x-2' width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <mask id="mask0_498_633" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                      <rect width="20" height="20" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_498_633)">
                      <path d="M2.91699 16.0416V3.95825L17.2599 9.99992L2.91699 16.0416ZM4.16699 14.1666L14.042 9.99992L4.16699 5.83325V8.91013L8.68616 9.99992L4.16699 11.0897V14.1666Z" fill="white" />
                    </g>
                  </svg>
                </button>
              </form>
              <hr className='border-[#4C4C4C] mt-[7px]' />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketChatPage;